import type { Dayjs } from '~/services/TimeService';

import type {
  CreateCommissionParams,
  CreateRewardParams,
  Reward,
  RewardConditionOperator,
  RewardConditionPayload,
  RewardConditionType,
  RewardTargetAudience,
  RewardType,
} from '~/types/services/agentHub';

import { serializeCommissionRuleBoundaryDate } from '~/utils/commissionDates';

import { CONDITION_OPERATOR_SYMBOL } from '../Commission/AgentHubCommissions/CommissionDialog.types';

export type RewardConditionForm = {
  id: string;
  type: RewardConditionType;
  operator: RewardConditionOperator;
  threshold: string;
};

export type ValidateRewardFormInput = {
  name: string;
  description: string;
  rewardType: RewardType;
  targetAudience: RewardTargetAudience;
  operator: 'AND' | 'OR';
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  expiryDate: Dayjs | null;
  conditions: Array<RewardConditionForm>;
  commission?: CreateCommissionParams;
};

export type ValidateRewardFormResult = { ok: true; payload: CreateRewardParams } | { ok: false; error: string };

export const REWARD_TARGET_AUDIENCES: Array<RewardTargetAudience> = ['agent', 'agency', 'affiliation'];

export const REWARD_CONDITION_TYPES: Array<RewardConditionType> = [
  'productType',
  'bookingCount',
  'totalValue',
  'commissionAmount',
  'affiliation',
  'agency',
  'agent',
];

export const REWARD_PRODUCT_TYPES = [
  'tour',
  'hotel',
  'rental',
  'bedbank_hotel',
  'tactical_ao_hotel',
  'car_hire',
  'custom_offer',
  'cruise',
  'addon',
  'experience',
  'flight',
  'tour_optional_experience',
];

export const REWARD_CONDITION_OPERATORS: Array<RewardConditionOperator> = ['eq', 'gt', 'gte', 'lt', 'lte'];

export const SUBJECT_REWARD_CONDITION_TYPES = new Set<RewardConditionType>(['affiliation', 'agency', 'agent']);

export function isEqOnlyRewardConditionType(type: RewardConditionType): boolean {
  return type === 'productType' || SUBJECT_REWARD_CONDITION_TYPES.has(type);
}

export const NUMERIC_REWARD_CONDITION_TYPES = new Set<RewardConditionType>([
  'bookingCount',
  'totalValue',
  'commissionAmount',
]);

export const REWARD_CONDITION_LABELS: Record<RewardConditionType, string> = {
  productType: 'Product type',
  bookingCount: 'Booking count',
  totalValue: 'TTV',
  commissionAmount: 'Commission amount',
  affiliation: 'Affiliation',
  agency: 'Agency',
  agent: 'Agent',
};

const MONEY_REWARD_CONDITION_TYPES = new Set<RewardConditionType>(['totalValue', 'commissionAmount']);

export type RewardConditionBreakdownInput = {
  type: RewardConditionType;
  operator: string;
  threshold: string;
};

function formatRewardBreakdownThreshold(type: RewardConditionType, threshold: string) {
  const trimmed = threshold.trim();
  if (!trimmed) {
    return '';
  }
  if (MONEY_REWARD_CONDITION_TYPES.has(type) && !trimmed.startsWith('$')) {
    return `$${trimmed}`;
  }
  return trimmed;
}

export function buildRewardConditionsSummary(
  conditions: Array<RewardConditionBreakdownInput>,
  operator: Reward['operator'],
) {
  const products = [
    ...new Set(
      conditions
        .filter((condition) => condition.type === 'productType')
        .map((condition) => condition.threshold)
        .filter(Boolean),
    ),
  ];
  const qualifications = buildRewardConditionBreakdown(
    conditions.filter((condition) => condition.type !== 'productType'),
  ).join(` ${operator} `);
  return [products.length ? `Product type: any of ${products.join(', ')}` : '', qualifications]
    .filter(Boolean)
    .join('; ');
}

export function buildRewardConditionBreakdown(conditions: Array<RewardConditionBreakdownInput>) {
  return conditions
    .map((condition) => {
      const threshold = formatRewardBreakdownThreshold(condition.type, condition.threshold);
      if (!threshold) {
        return '';
      }
      const operatorSymbol =
        CONDITION_OPERATOR_SYMBOL[condition.operator as keyof typeof CONDITION_OPERATOR_SYMBOL] ?? condition.operator;
      const label = REWARD_CONDITION_LABELS[condition.type] ?? condition.type;
      return `${label} ${operatorSymbol} ${threshold}`;
    })
    .filter(Boolean);
}

export function buildRewardCommissionDescription(rewardDescription: string) {
  const trimmed = rewardDescription.trim();
  return trimmed ? `Reward campaign: ${trimmed}` : '';
}

export function createEmptyRewardCondition(id: string): RewardConditionForm {
  return {
    id,
    type: 'bookingCount',
    operator: 'gte',
    threshold: '',
  };
}

export function validateRewardForm(input: ValidateRewardFormInput): ValidateRewardFormResult {
  if (!input.name.trim()) {
    return { ok: false, error: 'Name is required' };
  }
  if (!input.description.trim()) {
    return { ok: false, error: 'Description is required' };
  }
  if (!input.startDate || !input.endDate || !input.expiryDate) {
    return { ok: false, error: 'Start date, end date and expiry date are required' };
  }
  if (!input.startDate.isBefore(input.endDate, 'day')) {
    return { ok: false, error: 'Start date must be before end date' };
  }
  if (input.expiryDate.isBefore(input.endDate, 'day')) {
    return { ok: false, error: 'Expiry date must be on or after the reward end date' };
  }
  if (!input.conditions.some((condition) => condition.type !== 'productType')) {
    return { ok: false, error: 'At least one qualification condition is required in addition to product filters' };
  }
  if (
    input.conditions.some(
      (condition) =>
        condition.type === 'productType' &&
        (condition.operator !== 'eq' || !REWARD_PRODUCT_TYPES.includes(condition.threshold.trim())),
    )
  ) {
    return { ok: false, error: 'Select a supported product type with the equals operator' };
  }
  if (input.conditions.some((condition) => !condition.threshold.trim())) {
    return { ok: false, error: 'Every reward condition needs a threshold' };
  }
  if (
    input.conditions.some((condition) => {
      if (!NUMERIC_REWARD_CONDITION_TYPES.has(condition.type)) {
        return false;
      }
      const threshold = Number(condition.threshold);
      return Number.isNaN(threshold) || !Number.isFinite(threshold);
    })
  ) {
    return { ok: false, error: 'Booking count, TTV and commission amount must be numbers' };
  }
  if (input.rewardType !== 'commission') {
    return { ok: false, error: `Reward type ${input.rewardType} is not supported` };
  }
  if (!input.commission) {
    return { ok: false, error: 'A commission payload is required when rewardType is commission' };
  }

  const conditions: Array<RewardConditionPayload> = input.conditions.map((condition) => ({
    type: condition.type,
    operator: isEqOnlyRewardConditionType(condition.type) ? 'eq' : condition.operator,
    threshold: condition.threshold.trim(),
  }));

  return {
    ok: true,
    payload: {
      name: input.name.trim(),
      description: input.description.trim(),
      rewardType: input.rewardType,
      targetAudience: input.targetAudience,
      operator: input.operator,
      startDate: serializeCommissionRuleBoundaryDate(input.startDate),
      endDate: serializeCommissionRuleBoundaryDate(input.endDate),
      expiryDate: serializeCommissionRuleBoundaryDate(input.expiryDate),
      conditions,
      commission: {
        ...input.commission,
        type: 'incentive',
        description: buildRewardCommissionDescription(input.description),
        startDate: input.commission.startDate ?? serializeCommissionRuleBoundaryDate(input.startDate),
        endDate: serializeCommissionRuleBoundaryDate(input.expiryDate),
      },
    },
  };
}
